import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Form} from 'formik'
import {FieldComponent} from 'components/Shared/FieldComponent'
import {DatePickerFormFieldInline} from 'components/Shared/DatePickerInline'
import {FormColumn, FormRow, TitleBar} from 'components/Shared/CrudComponents'
import {Loader} from 'components/Shared/Loader'
import {FormikForm} from 'components/Shared/FormikForm'
import {
  CONTRIBUTIONS_COUNT_LIMIT, hasOCP, hasOCS,
  InsurancePolicySchema,
  POLICY_TYPES
} from 'components/Contractors/InsurancePolicy/Form/form'
import Sections from 'components/Contractors/InsurancePolicy/sections'
import Module from 'constants/customFieldModules'
import {PriceFields} from 'components/Contractors/InsurancePolicy/Form/PriceFields'
import {ContributionsFieldsList} from 'components/Contractors/InsurancePolicy/Form/ContributionsFieldsList'
import {FilesDropzone} from 'components/Shared/FilesDropzone'
import {getInsurancePolicyFile, uploadFile} from 'services/InsurancePolicyService'
import {FilesPreviewDialog} from 'components/Shared/FilesPreviewDialog'
import styles from 'components/Contractors/InsurancePolicy/Form/InsurancePolicyForm.module.css'
import {
  CONTRACTOR_POLICY_ATTACHMENTS_READ,
  CONTRACTOR_POLICY_ATTACHMENTS_WRITE, CONTRACTOR_POLICY_CONTRIBUTIONS_READ,
  CONTRACTOR_POLICY_CONTRIBUTIONS_WRITE,
  CONTRACTOR_POLICY_DATA_READ,
  CONTRACTOR_POLICY_DATA_WRITE,
  useAuth
} from 'context/auth'
import {ContractorSelectField} from "../../../Carrier/CarrierSelectField";
import ContractorRole from "../../../../constants/ContractorRole";
import {CheckboxField} from "../../../Shared/CheckboxField";
import {SelectField} from "../../../Shared/SelectField";
import {CustomWarningMessageText} from "../../../Shared/ErrorMessage";
import {POLICY_CONFIRMATION_FILE_TYPE, POLICY_FILE_TYPE} from "../constants";
import moment from "moment";

export function InsurancePolicyForm({
                                      initialValues,
                                      initialPolicyFiles,
                                      initialConfirmationFiles,
                                      entityId,
                                      onSubmit,
                                      loading,
                                      title
                                    }) {
  const {t} = useTranslation()
  const {hasAnyPrivileges} = useAuth()
  const [totalAmount, setTotalAmount] = useState('')

  const [previewedFile, setPreviewedFile] = useState(null)
  const [uploadedPolicyFiles, setUploadedPolicyFiles] = useState(initialPolicyFiles)
  const [uploadedConfirmationFiles, setUploadedConfirmationFiles] = useState(initialConfirmationFiles)

  useEffect(() => setUploadedPolicyFiles(initialPolicyFiles), [initialPolicyFiles])
  useEffect(() => setUploadedConfirmationFiles(initialConfirmationFiles), [initialConfirmationFiles])

  const setPreviewFile = ({id: fileId}) => {
    const previewedFile = fileId ? [...(uploadedPolicyFiles || []), ...(uploadedConfirmationFiles || [])].find(file => file.id === fileId) : null
    setPreviewedFile(previewedFile)
  }

  const handlePolicyFilesUpdate = setFieldValue => updatedPolicyFiles => {
    setFieldValue('filesIds', [...updatedPolicyFiles, ...uploadedConfirmationFiles].map(file => file.id))
    setUploadedPolicyFiles(updatedPolicyFiles.map(file => ({id: file.id, filename: file.filename})))
  }
  const handleConfirmationFilesUpdate = setFieldValue => updatedConfirmationFiles => {
    setFieldValue('filesIds', [...uploadedPolicyFiles, ...updatedConfirmationFiles].map(file => file.id))
    setUploadedConfirmationFiles(updatedConfirmationFiles.map(file => ({id: file.id, filename: file.filename})))
  }

  function submitForm(values, onSuccess) {
    if (!hasOCP(values.type)) {
      values.ocpAmount = null
      values.contributionsDetails.ocpAmount = null
    }
    if (!hasOCS(values.type)) {
      values.ocsAmount = null
      values.contributionsDetails.ocsAmount = null
    }
    onSubmit(values, onSuccess)
  }

  function checkContributionsSum(values) {
    if(values?.type){
      const ocp = hasOCP(values.type) ? +values.contributionsDetails.ocpAmount : 0
      let ocs = hasOCS(values.type) ? +values.contributionsDetails.ocsAmount : 0
      setTotalAmount(ocp+ocs || '')

      if (values.contributionsDetails.amount && +values.contributionsDetails.amount !== ocp + ocs) {
        return t('insurancePolicy.form.contributionsSumWarning');
      }
    }
    return ''
  }

  return <>
    <FilesPreviewDialog open={!!previewedFile}
                        fileToShow={previewedFile}
                        filesSource={getInsurancePolicyFile}
                        files={uploadedPolicyFiles}
                        onClose={() => setPreviewedFile(null)}
                        onFileChanged={setPreviewFile}/>
    <FormikForm
      initialValues={initialValues}
      validationSchema={InsurancePolicySchema}
      validateOnChange={false}
      validateOnBlur
      enableReinitialize
      onSubmit={submitForm}
      customFields={{
        module: Module.ContractorPolicies,
        entityId: entityId
      }}>
      {({setFieldValue, values}) => (
        <Form>
          <TitleBar
            buttonLabel={t('insurancePolicy.form.submit')}
            buttonDisabled={loading}
            title={title}/>
          <FormRow>
            <FormColumn section={Sections.BasicDetails}
                        disabled={!hasAnyPrivileges([CONTRACTOR_POLICY_DATA_WRITE])}
                        visible={hasAnyPrivileges([CONTRACTOR_POLICY_DATA_READ])}>
              <FieldComponent fieldName={'number'} label={'insurancePolicy.form.number'}/>
              <DatePickerFormFieldInline name={'dateOfConclusion'} label={'insurancePolicy.form.dateOfConclusion'}/>
              <DatePickerFormFieldInline name={'startDate'} onChange={value =>{
                setFieldValue('endDate', moment(value).add(1,'year').subtract(1, 'day'))
              }}  label={'insurancePolicy.form.startDate'}/>
              <DatePickerFormFieldInline name={'endDate'}  label={'insurancePolicy.form.endDate'}/>
              <ContractorSelectField
                fieldName={`insurerId`}
                label={'insurancePolicy.form.insurer'}
                creationRequiredRoles={[ContractorRole.INSURER]}
                role={ContractorRole.INSURER}
              />
              <CheckboxField name={`domestic`} label={'insurancePolicy.form.domestic'}/>
              <CheckboxField name={'foreign'} label={'insurancePolicy.form.foreign'}/>
            </FormColumn>
            <FormColumn section={Sections.AmountsDetails}
                        disabled={!hasAnyPrivileges([CONTRACTOR_POLICY_DATA_WRITE])}
                        visible={hasAnyPrivileges([CONTRACTOR_POLICY_DATA_READ])}>
              <SelectField
                data={POLICY_TYPES}
                label={t('insurancePolicy.form.type')}
                fieldName={'type'}
                setFieldValue={(fieldName, value) => {
                  setFieldValue(fieldName, value)
                }}
                initValue={POLICY_TYPES.find(t => t.id === values.type)}
              />
               <div className={hasOCP(values.type) ? styles.visible:styles.hidden}>
                <PriceFields amountFieldName={'ocpAmount.amount'}
                             amountLabel={'insurancePolicy.form.amount.ocpAmount'}
                             currencyCodeFieldName={'ocpAmount.currencyCode'}
                             currencyCodeLabel={'insurancePolicy.form.amount.currencyCode'}/>
                <PriceFields amountFieldName={'contributionsDetails.ocpAmount'}
                             amountLabel={'insurancePolicy.form.amount.ocpContribution'}
                             currencyCodeFieldName={'contributionsDetails.currencyCode'}
                             currencyCodeLabel={'insurancePolicy.form.amount.currencyCode'}/>
              </div>
               <div className={hasOCS(values.type) ? styles.visible:styles.hidden}>
                <PriceFields amountFieldName={'ocsAmount.amount'}
                             amountLabel={'insurancePolicy.form.amount.ocsAmount'}
                             currencyCodeFieldName={'ocsAmount.currencyCode'}
                             currencyCodeLabel={'insurancePolicy.form.amount.currencyCode'}/>
                <PriceFields amountFieldName={'contributionsDetails.ocsAmount'}
                             amountLabel={'insurancePolicy.form.amount.ocsContribution'}
                             currencyCodeFieldName={'contributionsDetails.currencyCode'}
                             currencyCodeLabel={'insurancePolicy.form.amount.currencyCode'}
                />
              </div>

            </FormColumn>
            <FormColumn section={Sections.ContributionsDetails}
                        disabled={!hasAnyPrivileges([CONTRACTOR_POLICY_CONTRIBUTIONS_WRITE])}
                        visible={hasAnyPrivileges([CONTRACTOR_POLICY_CONTRIBUTIONS_READ])}>
              <PriceFields amountFieldName={'contributionsDetails.amount'}
                           amountLabel={'insurancePolicy.form.contributionsDetails.amount'}
                           currencyCodeFieldName={'contributionsDetails.currencyCode'}
                           currencyCodeLabel={'insurancePolicy.form.contributionsDetails.currencyCode'}
                           disabled={true}
                           autoValue={totalAmount}
              />
              <CustomWarningMessageText message={checkContributionsSum(values)}/>
              <FieldComponent fieldName={'contributionsDetails.count'}
                              label={'insurancePolicy.form.contributionsDetails.count'}
                              type="number"
                              inputProps={{min: 0, max: CONTRIBUTIONS_COUNT_LIMIT}}

              />
              <ContributionsFieldsList/>
            </FormColumn>

            <FormColumn section={Sections.Files} className={styles.dropzoneSection}
                        disabled={!hasAnyPrivileges([CONTRACTOR_POLICY_ATTACHMENTS_WRITE])}
                        visible={hasAnyPrivileges([CONTRACTOR_POLICY_ATTACHMENTS_READ])}>
              <FilesDropzone initialFiles={initialPolicyFiles}
                             label={'insurancePolicy.form.policies'}
                             onUpload={file => uploadFile(file, POLICY_FILE_TYPE)}
                             onFilesUpdated={handlePolicyFilesUpdate(setFieldValue)}
                             onFileShow={setPreviewFile}/>
              <FilesDropzone initialFiles={initialConfirmationFiles}
                             label={'insurancePolicy.form.paymentConfirmations'}
                             onUpload={file => uploadFile(file, POLICY_CONFIRMATION_FILE_TYPE)}
                             onFilesUpdated={handleConfirmationFilesUpdate(setFieldValue)}
                             onFileShow={setPreviewFile}/>
            </FormColumn>
          </FormRow>
        </Form>
      )}
    </FormikForm>
    {loading && <Loader/>}
  </>
}
